/**
 * Scribe – moteur de l'enregistreur de réunions (sans React, testé avec des doublures : web/test/scribe-moteur.test.ts).
 *
 * - MediaRecorder en Opus (`audio/webm;codecs=opus`, sinon `audio/ogg;codecs=opus`), une partie toutes les 10 s
 *   (`timeslice` = DUREE_PARTIE_MS). Chaque partie est d'abord écrite dans le stockage local (IndexedDB, stockage.ts),
 *   puis déposée : POST …/parties → formulaire pré-signé → envoi direct dans S3 ; effacée une fois déposée.
 * - Séquence = un MediaRecorder, donc un fichier audio complet à lui seul : une nouvelle toutes les 10 min
 *   (DUREE_SEQUENCE_MS) et à chaque reprise (rechargement, micro débranché). La nouvelle démarre avant l'arrêt de
 *   l'ancienne : pas de trou entre deux séquences.
 * - Réseau coupé : les parties attendent dans le stockage local, nouvelle tentative après 2, 5, 10, 20 puis 30 s (et tout
 *   de suite au retour du réseau, `relancer`) ; l'état dit « hors ligne » et le nombre de parties en attente.
 * - Arrêt : dernière partie écrite, parties en attente déposées, puis POST …/fin ; la réunion passe en traitement.
 * - Refus de l'API : plafond de la semaine atteint → arrêt et fin avec ce qui a été reçu (avis `quota`) ; réunion qui
 *   n'enregistre plus (arrêtée par le serveur, supprimée) → arrêt ; partie refusée (validation) → partie abandonnée.
 * - Au chargement de la page : une réunion inachevée de l'utilisateur est proposée (« Reprendre » : nouvelle séquence ;
 *   « Terminer » : dépôt de ce qui reste puis fin) ; un arrêt déjà demandé se termine de lui-même.
 * - Pause sur silence (lot 2) : après 60 s sans son (détecteur de prise-de-son.ts), `mettreEnPause` met le
 *   MediaRecorder en pause – même séquence, un flux en pause puis repris reste un seul fichier valide – et
 *   `reprendreSon` le relance au premier son. Le temps de pause n'est ni déposé ni compté (durée des parties, plafond de
 *   la semaine) ; la rotation des 10 min échue pendant la pause a lieu à la reprise.
 * - Micro changé (lot 2) : `changerFlux` ouvre une nouvelle séquence sur le nouveau flux sans arrêter la réunion ; un
 *   micro perdu (débranché, coupé par le système) est remplacé par `remplacerFlux` (le micro choisi s'il est encore
 *   là, sinon celui par défaut), sinon la réunion est à reprendre.
 */
import {
  DUREE_MAX_REUNION_S, DUREE_PARTIE_MS, DUREE_SEQUENCE_MS, PARTIES_PAR_SEQUENCE_MAX, SEQUENCES_MAX, type TypePartieScribe,
} from '@agora/shared';
import { stockageMemoire, type PartieScribe, type SessionScribe, type StockageScribe } from './stockage';

/**
 * `inactif` : rien en cours (démarrage possible) ; `enregistrement` : le micro enregistre ; `envoi` : arrêt demandé,
 * dernières parties et fin en cours d'envoi ; `traitement` : fin reçue par le serveur (transcription puis compte-rendu) ;
 * `a-reprendre` : réunion inachevée retrouvée (rechargement, plantage, micro débranché).
 */
export type PhaseScribe = 'inactif' | 'enregistrement' | 'envoi' | 'traitement' | 'a-reprendre';

/**
 * Avis à montrer : `quota` (plafond de la semaine atteint : enregistrement arrêté), `reunion` (la réunion n'enregistre
 * plus ou n'existe plus), `duree` (DUREE_MAX_REUNION_S atteinte : fenêtre FenetreDureeMaximale), `micro` (micro coupé
 * ou débranché), `stockage` (stockage local indisponible : l'enregistrement continue en mémoire, sans reprise après un
 * rechargement).
 */
export type AvisScribe = 'quota' | 'reunion' | 'duree' | 'micro' | 'stockage';

export interface EtatScribe {
  phase: PhaseScribe;
  /** `distance` : la réunion enregistre aussi le son des participants à distance (partage d'écran de Teams). */
  reunion: { id: string; titre: string; distance?: boolean } | null;
  /** Durée enregistrée jusqu'à la dernière partie, en millisecondes. */
  dureeMs: number;
  /** Instant de la dernière partie (ou du début) pendant l'enregistrement : le chronomètre part de là. */
  depuis: number | null;
  /** Enregistrement en pause (silence de 60 s) : il reprend au premier son. */
  pause: boolean;
  /** Début de la pause : le chronomètre s'y arrête. */
  pauseDepuis: number | null;
  /** Parties pas encore déposées. */
  enAttente: number;
  horsLigne: boolean;
  avis: AvisScribe | null;
}

export const ETAT_INITIAL: EtatScribe = {
  phase: 'inactif', reunion: null, dureeMs: 0, depuis: null, pause: false, pauseDepuis: null, enAttente: 0, horsLigne: false, avis: null,
};

/** Durée affichée (chronomètre) à l'instant `maintenant` ; arrêtée pendant une pause. */
export function dureeAffichee(e: EtatScribe, maintenant: number): number {
  if (e.phase !== 'enregistrement' || e.depuis === null) return e.dureeMs;
  const fin = e.pause && e.pauseDepuis !== null ? e.pauseDepuis : maintenant;
  return e.dureeMs + Math.max(0, fin - e.depuis);
}

/** « 00:42:15 » (heures, minutes, secondes). */
export function formaterChrono(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return [Math.floor(s / 3600), Math.floor(s / 60) % 60, s % 60].map((n) => String(n).padStart(2, '0')).join(':');
}

/**
 * Durée lisible (« 35 s », « 42 min », « 1 h 20 min », « 10 h ») avec les unités Intl de la langue : `nombre` est
 * celui de useFormats().
 */
export function dureeLisible(secondes: number, nombre: (n: number, o?: Intl.NumberFormatOptions) => string): string {
  const unite = (n: number, unit: 'hour' | 'minute' | 'second') => nombre(n, { style: 'unit', unit, unitDisplay: 'short' });
  const s = Math.max(0, Math.round(secondes));
  if (s < 60) return unite(s, 'second');
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (!h) return unite(m, 'minute');
  return m ? `${unite(h, 'hour')} ${unite(m, 'minute')}` : unite(h, 'hour');
}

export interface FormatAudio { mime: string; type: TypePartieScribe }
/**
 * Formats essayés dans l'ordre (MediaRecorder.isTypeSupported) : Opus en WebM (Edge, Chrome, Safari depuis la 18.4 sur
 * macOS, iOS et iPadOS), puis en Ogg (Firefox). Safari avant la 18.4 n'enregistre qu'en MP4/AAC, que ni le serveur ni
 * Voxtral n'acceptent : aucun format, message « navigateur non pris en charge ».
 */
export const FORMATS_AUDIO: readonly FormatAudio[] = [
  { mime: 'audio/webm;codecs=opus', type: 'audio/webm' },
  { mime: 'audio/ogg;codecs=opus', type: 'audio/ogg' },
];

/** Format d'enregistrement du navigateur, ou null s'il n'enregistre pas l'Opus (Safari avant la 18.4). */
export function formatAudio(estPris: ((mime: string) => boolean) | undefined): FormatAudio | null {
  if (!estPris) return null;
  return FORMATS_AUDIO.find((f) => { try { return estPris(f.mime); } catch { return false; } }) ?? null;
}

/** Ce que Scribe utilise de MediaRecorder (doublure dans les tests). */
export interface Magnetophone {
  readonly state: 'inactive' | 'recording' | 'paused';
  start(timeslice: number): void;
  stop(): void;
  /** Pause sur silence : le flux reste un seul fichier ; aucune donnée n'est produite pendant la pause. */
  pause(): void;
  resume(): void;
  ondataavailable: ((e: { data: Blob }) => void) | null;
  onstop: (() => void) | null;
}

/** Décision après un refus : `quota`, `reunion` (n'enregistre plus, introuvable), `partie` (partie refusée), `reessayer`. */
export type Refus = 'quota' | 'reunion' | 'partie' | 'reessayer';

export interface DependancesScribe {
  /** Stockage local (ouvert au premier usage). */
  stockage: () => Promise<StockageScribe>;
  /** Dépose une partie (formulaire pré-signé puis S3) ; rejette en cas d'échec. */
  deposer: (p: PartieScribe) => Promise<void>;
  /** POST …/fin (une réunion déjà arrêtée par le serveur compte comme terminée) ; rejette en cas d'échec. */
  terminer: (reunion: string) => Promise<void>;
  /** Décision selon l'erreur d'un dépôt ou de la fin. */
  classer: (e: unknown) => Refus;
  creerMagnetophone: (flux: MediaStream, mime: string) => Magnetophone;
  maintenant?: () => number;
  /** Minuteur annulable (rotation des séquences, nouvelle tentative). */
  attendre?: (ms: number, f: () => void) => () => void;
  /** La fin a été reçue par le serveur. */
  apresFin?: (reunion: string) => void;
  /**
   * Micro perdu pendant l'enregistrement (débranché, coupé par le système) : flux de remplacement à enregistrer (le
   * micro choisi s'il est encore là, sinon celui par défaut ; avec le son de Teams, le mélange), ou null (aucun micro :
   * la réunion est à reprendre).
   */
  remplacerFlux?: () => Promise<MediaStream | null>;
}

/** Délais avant une nouvelle tentative de dépôt (réseau coupé, serveur indisponible). */
export const DELAIS_REPRISE_MS = [2000, 5000, 10_000, 20_000, 30_000] as const;
/** Arrêt d'un MediaRecorder : la dernière partie arrive avant l'évènement `stop` ; au-delà, on n'attend plus. */
const ATTENTE_ARRET_MS = 3000;

/**
 * Séquence en cours : `dernier` = instant de la dernière partie ; `pauseMs` = temps de pause depuis cette partie, et
 * `pauseDebut` = début de la pause en cours (la durée d'une partie n'en tient pas compte).
 */
interface Sequence { magneto: Magnetophone; sequence: number; numero: number; dernier: number; pauseMs: number; pauseDebut: number | null }

const attendreParDefaut = (ms: number, f: () => void) => {
  const id = setTimeout(f, ms);
  return () => clearTimeout(id);
};

export class MoteurScribe {
  private etat: EtatScribe = ETAT_INITIAL;
  private readonly abonnes = new Set<(e: EtatScribe) => void>();
  private stockage: StockageScribe | null = null;
  private session: SessionScribe | null = null;
  private flux: MediaStream | null = null;
  private format: FormatAudio | null = null;
  private courant: Sequence | null = null;
  private annulerRotation: (() => void) | null = null;
  private annulerReprise: (() => void) | null = null;
  /** Rotation des 10 min échue pendant une pause : faite à la reprise du son. */
  private rotationDue = false;
  /** Remplacement d'un micro perdu en cours (un seul à la fois). */
  private remplacement: Promise<void> | null = null;
  private tentative = 0;
  private ecritures: Promise<void> = Promise.resolve();
  private pompe: Promise<void> | null = null;
  private encore = false;
  private readonly maintenant: () => number;
  private readonly attendre: (ms: number, f: () => void) => () => void;

  constructor(private readonly deps: DependancesScribe) {
    this.maintenant = deps.maintenant ?? Date.now;
    this.attendre = deps.attendre ?? attendreParDefaut;
  }

  lire(): EtatScribe { return this.etat; }

  abonner(f: (e: EtatScribe) => void): () => void {
    this.abonnes.add(f);
    f(this.etat);
    return () => { this.abonnes.delete(f); };
  }

  /** Stockage persistant (une réunion survit au rechargement de la page) ; faux avant le premier usage. */
  get persistant(): boolean { return !!this.stockage?.persistant; }

  private maj(p: Partial<EtatScribe>) {
    this.etat = { ...this.etat, ...p };
    for (const f of this.abonnes) f(this.etat);
  }

  private async base(): Promise<StockageScribe> {
    this.stockage ??= await this.deps.stockage();
    return this.stockage;
  }

  /** Au chargement : la réunion inachevée la plus récente de l'utilisateur, à reprendre (ou à terminer si l'arrêt était demandé). */
  async charger(utilisateur: string): Promise<void> {
    if (this.etat.phase !== 'inactif' && this.etat.phase !== 'traitement') return;
    const st = await this.base();
    const [s] = (await st.sessions(utilisateur)).sort((a, b) => b.debut.localeCompare(a.debut));
    if (!s || (this.etat.phase !== 'inactif' && this.etat.phase !== 'traitement')) return;
    this.session = s;
    const enAttente = (await st.parties(s.reunion)).length;
    this.maj({
      phase: s.terminee ? 'envoi' : 'a-reprendre', reunion: { id: s.reunion, titre: s.titre, distance: !!s.distance }, dureeMs: s.dureeMs,
      depuis: null, pause: false, pauseDepuis: null, enAttente, horsLigne: false, avis: null,
    });
    if (s.terminee) this.pomper();
  }

  /**
   * Démarre l'enregistrement d'une réunion créée sur le serveur (séquence 0). `distance` : le flux mêle le son de Teams
   * (gardé avec la session : après un rechargement, le partage est à refaire).
   */
  async demarrer(o: { reunion: string; titre: string; utilisateur: string; flux: MediaStream; format: FormatAudio; distance?: boolean }): Promise<void> {
    if (this.etat.phase !== 'inactif' && this.etat.phase !== 'traitement') throw new Error('Scribe enregistre déjà');
    const st = await this.base();
    this.session = {
      reunion: o.reunion, utilisateur: o.utilisateur, titre: o.titre, type: o.format.type, sequence: 0, dureeMs: 0,
      debut: new Date(this.maintenant()).toISOString(), terminee: false, distance: !!o.distance,
    };
    await this.ecrireSession(st);
    this.flux = o.flux;
    this.format = o.format;
    this.tentative = 0;
    this.rotationDue = false;
    this.maj({
      phase: 'enregistrement', reunion: { id: o.reunion, titre: o.titre, distance: !!o.distance }, dureeMs: 0, depuis: this.maintenant(),
      pause: false, pauseDepuis: null, enAttente: 0, horsLigne: false, avis: null,
    });
    this.ouvrirSequence(0);
  }

  /** Reprend la réunion inachevée dans une nouvelle séquence ; les parties restées en attente partent aussi. */
  async reprendre(flux: MediaStream, format: FormatAudio, distance = false): Promise<void> {
    const s = this.session;
    if (this.etat.phase !== 'a-reprendre' || !s) return;
    const sequence = s.sequence + 1;
    if (sequence >= SEQUENCES_MAX || s.dureeMs >= DUREE_MAX_REUNION_S * 1000 - DUREE_PARTIE_MS) {
      flux.getTracks().forEach((t) => t.stop());
      await this.terminerReprise('duree');
      return;
    }
    this.session = { ...s, sequence, type: format.type, distance };
    await this.ecrireSession(await this.base());
    this.flux = flux;
    this.format = format;
    this.rotationDue = false;
    this.maj({
      phase: 'enregistrement', reunion: { id: s.reunion, titre: s.titre, distance }, depuis: this.maintenant(), pause: false, pauseDepuis: null, avis: null,
    });
    this.ouvrirSequence(sequence);
    this.pomper();
  }

  /**
   * Silence de 60 s : MediaRecorder en pause (même séquence, le flux reste un seul fichier valide). Rien n'est déposé ni
   * compté pendant la pause ; le chronomètre s'arrête.
   */
  mettreEnPause(): void {
    const c = this.courant;
    if (this.etat.phase !== 'enregistrement' || this.etat.pause || !c || c.magneto.state !== 'recording') return;
    try { c.magneto.pause(); } catch { return; }
    const t = this.maintenant();
    c.pauseDebut = t;
    this.maj({ pause: true, pauseDepuis: t });
  }

  /** Premier son après une pause (ou « Reprendre maintenant ») : l'enregistrement repart dans la même séquence. */
  reprendreSon(): void {
    if (!this.etat.pause) return;
    const c = this.courant;
    const t = this.maintenant();
    if (c && c.pauseDebut !== null) { c.pauseMs += t - c.pauseDebut; c.pauseDebut = null; }
    try { if (c && c.magneto.state === 'paused') c.magneto.resume(); } catch { /* arrêté entre-temps : la séquence suivante prendra le relais */ }
    const { depuis, pauseDepuis } = this.etat;
    this.maj({ pause: false, pauseDepuis: null, depuis: depuis !== null && pauseDepuis !== null ? depuis + (t - pauseDepuis) : t });
    if (this.rotationDue) { this.rotationDue = false; this.tourner(); }
  }

  /**
   * Micro changé pendant l'enregistrement (choix de l'utilisateur, micro perdu, son de Teams ajouté) : nouvelle séquence
   * sur `flux`, démarrée avant l'arrêt de la précédente ; résout quand l'ancienne séquence a écrit sa dernière partie
   * (l'appelant peut alors rendre l'ancien micro). `distance` : le flux mêle désormais le son de Teams. Faux si rien
   * n'enregistre.
   */
  async changerFlux(flux: MediaStream, distance?: boolean): Promise<boolean> {
    const ancien = this.courant;
    if (this.etat.phase !== 'enregistrement' || !this.session || !this.format || !ancien) return false;
    const suivante = ancien.sequence + 1;
    if (suivante >= SEQUENCES_MAX) { await this.arreter('duree'); return false; }
    // Une pause en cours prend fin (sans la rotation reportée : la nouvelle séquence la remplace) ; la nouvelle
    // séquence enregistre tout de suite.
    this.rotationDue = false;
    if (this.etat.pause) this.reprendreSon();
    this.session = { ...this.session, sequence: suivante, ...(distance !== undefined ? { distance } : {}) };
    void this.base().then((st) => this.ecrireSession(st));
    if (distance !== undefined && this.etat.reunion) this.maj({ reunion: { ...this.etat.reunion, distance } });
    this.flux = flux;
    this.ouvrirSequence(suivante);
    if (ancien.magneto.state === 'inactive') return true;
    await new Promise<void>((ok) => {
      const annuler = this.attendre(ATTENTE_ARRET_MS, ok);
      ancien.magneto.onstop = () => { annuler(); ok(); };
      try { ancien.magneto.stop(); } catch { annuler(); ok(); }
    });
    return true;
  }

  /**
   * Micro perdu (piste terminée, appareil retiré, MediaRecorder arrêté par le système) : un micro de remplacement
   * (`remplacerFlux`) prend le relais dans une nouvelle séquence ; sans micro, la réunion est à reprendre (avis `micro`).
   */
  microPerdu(): Promise<void> {
    this.remplacement ??= (async () => {
      const c = this.courant;
      if (this.etat.phase !== 'enregistrement' || !c) return;
      const flux = this.deps.remplacerFlux ? await this.deps.remplacerFlux().catch(() => null) : null;
      if (this.etat.phase !== 'enregistrement' || this.courant !== c) return;
      if (flux && await this.changerFlux(flux)) return;
      await this.interrompre();
    })().finally(() => { this.remplacement = null; });
    return this.remplacement;
  }

  /** « Terminer » une réunion inachevée : dépôt de ce qui reste, puis fin. */
  async terminerReprise(avis: AvisScribe | null = null): Promise<void> {
    if (this.etat.phase !== 'a-reprendre' || !this.session) return;
    await this.marquerTerminee();
    this.maj({ phase: 'envoi', depuis: null, avis });
    this.pomper();
  }

  /** Arrête l'enregistrement : dernière partie écrite, dépôt des parties en attente, puis fin. */
  async arreter(avis: AvisScribe | null = null): Promise<void> {
    if (this.etat.phase !== 'enregistrement') return;
    await this.couper();
    await this.marquerTerminee();
    this.maj({ phase: 'envoi', depuis: null, pause: false, pauseDepuis: null, avis });
    this.pomper();
  }

  /** Après le traitement (ou un avis) : prêt pour une nouvelle réunion. */
  effacer(): void {
    if (this.etat.phase === 'traitement' || this.etat.phase === 'inactif') this.maj(ETAT_INITIAL);
  }

  /** Oublie la réunion inachevée (elle n'enregistre plus sur le serveur) : parties locales effacées. */
  async oublier(avis: AvisScribe | null = null): Promise<void> {
    const s = this.session;
    await this.couper();
    this.annulerReprise?.();
    this.annulerReprise = null;
    if (s) await (await this.base()).oublier(s.reunion);
    this.session = null;
    this.maj({ ...ETAT_INITIAL, avis });
  }

  /** Nouvelle tentative de dépôt tout de suite (retour du réseau, de l'onglet). */
  relancer(): void {
    if (!this.session || (this.etat.phase !== 'enregistrement' && this.etat.phase !== 'envoi')) return;
    this.annulerReprise?.();
    this.annulerReprise = null;
    this.pomper();
  }

  /** Démontage (déconnexion, onglet fermé) : le micro est rendu ; la réunion reste à reprendre au prochain chargement. */
  liberer(): void {
    this.annulerRotation?.();
    this.annulerReprise?.();
    this.annulerRotation = this.annulerReprise = null;
    const c = this.courant;
    this.courant = null;
    try { if (c && c.magneto.state !== 'inactive') c.magneto.stop(); } catch { /* déjà arrêté */ }
    this.flux?.getTracks().forEach((t) => t.stop());
    this.flux = null;
  }

  // ---------------------------------------------------------------- enregistrement

  private ouvrirSequence(sequence: number) {
    if (!this.flux || !this.format) return;
    const format = this.format;
    const magneto = this.deps.creerMagnetophone(this.flux, format.mime);
    const c: Sequence = { magneto, sequence, numero: 0, dernier: this.maintenant(), pauseMs: 0, pauseDebut: null };
    magneto.ondataavailable = (e) => {
      const t = this.maintenant();
      // Temps enregistré depuis la partie précédente, pauses retirées (elles ne sont ni déposées ni comptées).
      const pause = c.pauseMs + (c.pauseDebut !== null ? t - c.pauseDebut : 0);
      const dureeMs = Math.min(DUREE_PARTIE_MS * 2, Math.max(1, Math.round(t - c.dernier - pause)));
      c.dernier = t;
      c.pauseMs = 0;
      if (c.pauseDebut !== null) c.pauseDebut = t;
      const s = this.session;
      if (!s || !e.data || e.data.size === 0 || c.numero >= PARTIES_PAR_SEQUENCE_MAX) return;
      this.ecrire({ reunion: s.reunion, sequence: c.sequence, numero: c.numero++, type: format.type, dureeMs, audio: e.data });
      if (this.etat.phase === 'enregistrement') {
        this.maj({
          dureeMs: this.etat.dureeMs + dureeMs, depuis: t, enAttente: this.etat.enAttente + 1, ...(this.etat.pause ? { pauseDepuis: t } : {}),
        });
        // Durée maximale d'une réunion (au-delà, une nouvelle réunion) : arrêt avant le refus du serveur.
        if (this.etat.dureeMs >= DUREE_MAX_REUNION_S * 1000 - DUREE_PARTIE_MS) void this.arreter('duree');
      }
    };
    // Arrêt que Scribe n'a pas demandé (micro débranché, coupé par le système) : un autre micro prend le relais, sinon
    // la réunion est à reprendre.
    magneto.onstop = () => { if (this.courant === c) void this.microPerdu(); };
    this.courant = c;
    magneto.start(DUREE_PARTIE_MS);
    this.annulerRotation?.();
    this.annulerRotation = this.attendre(DUREE_SEQUENCE_MS, () => this.tourner());
  }

  /** Toutes les 10 min : nouvelle séquence (démarrée avant l'arrêt de la précédente) ; en pause, à la reprise du son. */
  private tourner() {
    const ancien = this.courant;
    this.annulerRotation = null;
    if (!ancien || this.etat.phase !== 'enregistrement' || !this.session) return;
    if (this.etat.pause) { this.rotationDue = true; return; }
    const suivante = ancien.sequence + 1;
    if (suivante >= SEQUENCES_MAX) { void this.arreter('duree'); return; }
    this.session = { ...this.session, sequence: suivante };
    void this.base().then((st) => this.ecrireSession(st));
    this.ouvrirSequence(suivante);
    try { ancien.magneto.stop(); } catch { /* déjà arrêté */ }
  }

  /** Arrête le MediaRecorder en cours (sa dernière partie est écrite) et rend le micro. */
  private async couper(): Promise<void> {
    this.annulerRotation?.();
    this.annulerRotation = null;
    this.rotationDue = false;
    const c = this.courant;
    this.courant = null;
    if (c && c.magneto.state !== 'inactive') {
      await new Promise<void>((ok) => {
        const annuler = this.attendre(ATTENTE_ARRET_MS, ok);
        c.magneto.onstop = () => { annuler(); ok(); };
        try { c.magneto.stop(); } catch { annuler(); ok(); }
      });
    }
    this.flux?.getTracks().forEach((t) => t.stop());
    this.flux = null;
    await this.ecritures;
  }

  private async interrompre() {
    await this.couper();
    if (this.session) this.maj({ phase: 'a-reprendre', depuis: null, pause: false, pauseDepuis: null, dureeMs: this.session.dureeMs, avis: 'micro' });
  }

  // ---------------------------------------------------------------- stockage local

  private ecrireSession(st: StockageScribe): Promise<void> {
    const s = this.session;
    if (!s) return Promise.resolve();
    this.ecritures = this.ecritures.then(() => st.ecrireSession({ ...s })).catch(() => this.basculerEnMemoire());
    return this.ecritures;
  }

  private ecrire(p: PartieScribe) {
    this.ecritures = this.ecritures
      .then(async () => {
        const st = await this.base();
        try {
          await st.ajouterPartie(p);
        } catch {
          await this.basculerEnMemoire();
          await this.stockage!.ajouterPartie(p);
        }
        if (this.session?.reunion === p.reunion) {
          this.session = { ...this.session, dureeMs: this.session.dureeMs + p.dureeMs, sequence: Math.max(this.session.sequence, p.sequence) };
          await this.stockage!.ecrireSession({ ...this.session }).catch(() => undefined);
        }
      })
      .catch(() => undefined);
    void this.ecritures.then(() => this.pomper());
  }

  /** IndexedDB en échec (disque plein, accès retiré) : la suite en mémoire, avis affiché. */
  private async basculerEnMemoire() {
    if (this.stockage && !this.stockage.persistant) return;
    this.stockage = stockageMemoire();
    if (this.session) await this.stockage.ecrireSession({ ...this.session });
    this.maj({ avis: 'stockage' });
  }

  private async marquerTerminee() {
    if (!this.session) return;
    this.session = { ...this.session, terminee: true };
    await this.ecrireSession(await this.base());
  }

  // ---------------------------------------------------------------- dépôt

  private pomper(): void {
    if (this.pompe) { this.encore = true; return; }
    this.pompe = this.deposerTout()
      .catch(() => undefined)
      .finally(() => {
        this.pompe = null;
        if (this.encore) { this.encore = false; this.pomper(); }
      });
  }

  private async deposerTout(): Promise<void> {
    await this.base();
    for (;;) {
      const s = this.session;
      if (!s) return;
      const attente = await this.stockage!.parties(s.reunion);
      if (attente.length !== this.etat.enAttente) this.maj({ enAttente: attente.length });
      // Nouvelle tentative déjà prévue (réseau coupé) : le compte est à jour, on attend son heure.
      if (this.annulerReprise) return;
      const p = attente[0];
      if (!p) break;
      try {
        await this.deps.deposer(p);
        await this.stockage!.retirerPartie(p);
        this.tentative = 0;
        if (this.etat.horsLigne) this.maj({ horsLigne: false });
      } catch (e) {
        const refus = this.deps.classer(e);
        if (refus === 'partie') { await this.stockage!.retirerPartie(p); continue; }
        if (refus === 'quota' || refus === 'reunion') { await this.refuser(refus); return; }
        this.reessayerPlusTard();
        return;
      }
    }
    if (this.etat.phase === 'envoi' && this.session) await this.finir();
  }

  /** Dépôt définitivement refusé : arrêt, parties restantes abandonnées, puis fin avec ce qui a été reçu. */
  private async refuser(avis: 'quota' | 'reunion') {
    const s = this.session;
    if (!s) return;
    if (this.etat.phase === 'enregistrement') await this.couper();
    for (const p of await this.stockage!.parties(s.reunion)) await this.stockage!.retirerPartie(p);
    await this.marquerTerminee();
    this.maj({ phase: 'envoi', depuis: null, pause: false, pauseDepuis: null, enAttente: 0, avis });
    await this.finir();
  }

  private async finir() {
    const s = this.session;
    if (!s) return;
    try {
      await this.deps.terminer(s.reunion);
    } catch (e) {
      if (this.deps.classer(e) === 'reessayer') { this.reessayerPlusTard(); return; }
      // Réunion introuvable (supprimée entre-temps) : rien à terminer.
      await this.stockage!.oublier(s.reunion);
      this.session = null;
      this.maj({ ...ETAT_INITIAL, avis: 'reunion' });
      return;
    }
    await this.stockage!.oublier(s.reunion);
    this.session = null;
    this.tentative = 0;
    this.maj({ phase: 'traitement', enAttente: 0, horsLigne: false, depuis: null });
    this.deps.apresFin?.(s.reunion);
  }

  private reessayerPlusTard() {
    this.maj({ horsLigne: true });
    this.annulerReprise?.();
    const ms = DELAIS_REPRISE_MS[Math.min(this.tentative, DELAIS_REPRISE_MS.length - 1)];
    this.tentative += 1;
    this.annulerReprise = this.attendre(ms, () => { this.annulerReprise = null; this.pomper(); });
  }
}
